/**
 * Quest-area world marker (M5).
 *
 * `ExtractBeacons` cannot take an extra marker — its only ingress is `setExtracts(ExtractSnap[])`,
 * whose element type carries no label and whose ids drive a full rebuild on every RaidState. So
 * this is a small sibling: one ground ring at the quest area plus a `.wm` label, sharing
 * `hud.markerRoot` and the `.wm`/`.wm .chev`/`.wm .dist` CSS that already exists in index.html,
 * so it needs no markup or stylesheet of its own.
 *
 * Deliberately cheap: one mesh, one label, rebuilt only when the area actually changes.
 */
import {
  Color,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  RingGeometry,
  Vector3,
  type PerspectiveCamera,
  type Scene,
} from 'three/webgpu';
import type { Vec3 } from '@stillsun/shared';

/** Requiem `--accent` (#9ff0c3): the quest ring reads as objective, not as extraction (amber). */
const ACCENT = 0x9ff0c3;
/** DOM label writes throttled the same way the extract beacons throttle theirs. */
const LABEL_INTERVAL = 1 / 20;
/** Label anchor above the ring's centre, in metres. */
const LABEL_HEIGHT = 4;
/** Ring line thickness, metres. */
const RING_THICKNESS = 0.45;

export interface QuestArea {
  name: string;
  pos: Vec3;
  radius: number;
}

export class QuestMarker {
  private readonly root = new Group();
  private readonly labelRoot: HTMLElement;
  private readonly label: HTMLDivElement;
  private readonly chev: HTMLDivElement;
  private readonly dist: HTMLDivElement;
  private readonly tmp = new Vector3();
  private readonly tmpView = new Vector3();

  private mesh: Mesh | null = null;
  private area: QuestArea | null = null;
  private key = '';
  private accum = 0;
  private lastMetres = -1;

  constructor(scene: Scene, labelRoot: HTMLElement) {
    this.root.name = 'questmarker';
    scene.add(this.root);
    this.labelRoot = labelRoot;

    const label = document.createElement('div');
    label.className = 'wm off';
    const chev = document.createElement('div');
    chev.className = 'chev';
    const dist = document.createElement('div');
    dist.className = 'dist';
    label.appendChild(chev);
    label.appendChild(dist);
    labelRoot.appendChild(label);
    this.label = label;
    this.chev = chev;
    this.dist = dist;
  }

  /** Point the marker at a quest area, or clear it with null. */
  set(area: QuestArea | null): void {
    const key = area ? `${area.name}|${area.pos.x},${area.pos.y},${area.pos.z}|${area.radius}` : '';
    if (key === this.key) return;
    this.key = key;
    this.area = area;
    this.disposeMesh();
    this.lastMetres = -1;
    if (!area) {
      this.label.classList.add('off');
      this.root.visible = false;
      return;
    }
    this.root.visible = true;
    this.chev.textContent = `◆ ${area.name.replace(/^the /i, '').toUpperCase()}`;

    const radius = Math.max(1, area.radius);
    const geo = new RingGeometry(Math.max(0.2, radius - RING_THICKNESS), radius, 96, 1);
    const mat = new MeshStandardNodeMaterial({
      color: 0x0e1a15,
      emissive: new Color(ACCENT),
      emissiveIntensity: 3,
      roughness: 0.6,
      metalness: 0,
      transparent: true,
      opacity: 0.7,
    });
    const mesh = new Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(area.pos.x, area.pos.y + 0.04, area.pos.z);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    this.root.add(mesh);
    this.mesh = mesh;
  }

  update(dt: number, camera: PerspectiveCamera, viewer: Vec3): void {
    const area = this.area;
    if (!area) return;
    this.accum += dt;
    if (this.accum < LABEL_INTERVAL) return;
    this.accum = 0;

    const w = this.labelRoot.clientWidth;
    const h = this.labelRoot.clientHeight;
    if (w === 0 || h === 0) return;

    this.tmp.set(area.pos.x, area.pos.y + LABEL_HEIGHT, area.pos.z);
    this.tmpView.copy(this.tmp).applyMatrix4(camera.matrixWorldInverse);
    let off = this.tmpView.z >= -camera.near;
    if (!off) {
      this.tmp.project(camera);
      off = Math.abs(this.tmp.x) > 1.15 || Math.abs(this.tmp.y) > 1.15;
    }
    if (off) {
      this.label.classList.add('off');
      return;
    }
    this.label.classList.remove('off');
    this.label.style.left = `${String(Math.round((this.tmp.x * 0.5 + 0.5) * w))}px`;
    this.label.style.top = `${String(Math.round((-this.tmp.y * 0.5 + 0.5) * h))}px`;

    const metres = Math.round(
      Math.hypot(area.pos.x - viewer.x, area.pos.y - viewer.y, area.pos.z - viewer.z),
    );
    if (metres !== this.lastMetres) {
      this.lastMetres = metres;
      this.dist.textContent = `${String(metres)} m`;
    }
  }

  clear(): void {
    this.set(null);
  }

  dispose(): void {
    this.disposeMesh();
    this.label.remove();
    this.root.removeFromParent();
  }

  private disposeMesh(): void {
    const mesh = this.mesh;
    if (!mesh) return;
    this.root.remove(mesh);
    mesh.geometry.dispose();
    (mesh.material as { dispose(): void }).dispose();
    this.mesh = null;
  }
}
